/**
 * Visual state for a filter trigger (the button that opens a filter
 * popover/menu), shared across every screen's filter so they stay consistent.
 *
 * Mirrors the `@kula-ai/components` `Button` variants without rebuilding each
 * Popover/Menu trigger:
 *   - empty   (no filters applied)  -> `quaternary` — neutral white/grey
 *   - applied (>= 1 filter applied) -> `tertiary`   — highlighted purple
 *
 * Product: a filter trigger should read as inactive until a filter is applied.
 * Token values are kept in lock-step with the kula-ai Button `quaternary` /
 * `tertiary` variants (and `theme/components/button.js`).
 */
export interface FilterTriggerStyles {
  /** Background colour token for the trigger surface. */
  bg: string;
  /** Colour token applied to both the label text and the icons. */
  contentColor: string;
  /** Border shorthand — only the empty (`quaternary`) state has a border. */
  border?: string;
  /** Border colour token — empty (`quaternary`) state only. */
  borderColor?: string;
}

const APPLIED_STYLES: FilterTriggerStyles = {
  bg: 'primary-50',
  contentColor: 'primary-600',
};

const EMPTY_STYLES: FilterTriggerStyles = {
  bg: 'base-white',
  contentColor: 'secondary-700',
  border: '1px solid',
  borderColor: 'secondary-100',
};

/**
 * Returns the style tokens for a filter trigger given whether any filter is
 * currently applied.
 *
 * @param isApplied - `true` when at least one filter is applied.
 */
export const getFilterTriggerStyles = (
  isApplied: boolean
): FilterTriggerStyles => (isApplied ? APPLIED_STYLES : EMPTY_STYLES);
